import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { communicationServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import { selectLoggedInUserState } from '@store';
import { FILTER_RULE_ENUMS } from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';
import { useSelectorRef } from 'hooks/common';
import {
  filtersModalConditionMyIssuesMailboxChange,
  filtersModalRulesMyIssuesMailboxChange,
  resetModalMyIssuesMailboxChange,
  selectMyIssuesMailboxState,
} from 'store/myIssuesMailbox/myIssuesMailboxSlice';
import { handleMailBoxes } from 'hooks/communication/utils';
import { getEmailListPayload, handleMailBoxData } from './utils';

const POLLING_INTERVAL = 2 * 60 * 1000; // = 120000 = 2 minutes
export const MY_ISSUES_MAILBOX_PAGE_SIZE = 200;

const useMyIssuesMailboxOperations = () => {
  const { isReady, query } = useRouter();
  const { filters } = useSelector(selectMyIssuesMailboxState);

  const currentUser = useSelectorRef(selectLoggedInUserState);
  const assignee = currentUser?.email;
  const dispatch = useDispatch();
  const {
    orderBy = 'messageDate',
    order = 'desc',
    labels,
    page = '1',
    teams = undefined,
    status = undefined,
    grouping = true,
    pageSize = MY_ISSUES_MAILBOX_PAGE_SIZE,
    unread = undefined,
    mailboxId = undefined,
    query: searchQuery = '',
  } = query;

  const mailBoxesParams = {
    orderBy: 'createdAt',
    order: 'desc',
    page: 1,
    pageSize: 1000,
  };
  const mailBoxesResponse = useQuery(
    [QUERY_KEYS.GET_MAILBOXES_KEY, mailBoxesParams],
    async () => {
      const payload = {
        type: FILTER_RULE_ENUMS.EmptyRuleType,
      };

      const response = await communicationServices.getEmailBoxes(
        mailBoxesParams,
        payload
      );

      return response;
    }
  );
  const mailBoxes = handleMailBoxes(mailBoxesResponse?.data?.data);

  const emailsResponse = useQuery(
    [
      QUERY_KEYS.GET_MY_ISSUES_MAILBOX_KEY,
      {
        page,
        orderBy,
        order,
        teams,
        status,
        labels,
        grouping,
        pageSize: MY_ISSUES_MAILBOX_PAGE_SIZE,
        assignee,
        filters: filters?.modalFiltersRules,
        unread,
        mailboxId,
        query: searchQuery,
      },
    ],
    () => {
      const payloadParams = {
        assignee,
        team: teams,
        status,
        labels,
        unread,
        mailboxId,
      };
      const params = {
        page,
        orderBy,
        order,
        pageSize: MY_ISSUES_MAILBOX_PAGE_SIZE,
        query: searchQuery,
      };
      const payload = getEmailListPayload(
        getCleanedUrlParams(payloadParams),
        filters?.modalFiltersRules
      );
      return communicationServices.getEmails({
        params: getCleanedUrlParams(params),
        payload,
      });
    },
    {
      refetchInterval: POLLING_INTERVAL,
      enabled: !!isReady && !!assignee && mailBoxesResponse.isFetched,
    }
  );
  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesMyIssuesMailboxChange(rules));
  };

  const resetModalChange = useCallback(() => {
    dispatch(resetModalMyIssuesMailboxChange());
  }, [dispatch]);

  const handleFiltersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionMyIssuesMailboxChange(conditions));
    },
    [dispatch]
  );
  useEffect(() => {
    if (emailsResponse?.error) {
      showError(emailsResponse.error);
    }
  }, [emailsResponse?.error]);
  const data = handleMailBoxData(emailsResponse?.data?.data || []);

  return {
    page,
    labels,
    pageSize: parseInt(pageSize, 10) || MY_ISSUES_MAILBOX_PAGE_SIZE,
    currentUser,
    loading: emailsResponse?.isLoading || mailBoxesResponse?.isLoading,
    total: emailsResponse?.data?.meta?.total,
    data: data || [],
    mailBoxes,
    refetch: emailsResponse.refetch,
    handleChangeRules,
    resetModalChange,
    handleFiltersModalChange,
  };
};

export default useMyIssuesMailboxOperations;
